/**
 * Kiosk API Service
 * 
 * This service provides methods to interact with the secure backend API
 * instead of making direct calls to the WooCommerce API.
 */

import { get, post, put, del } from '../utils/api';

/**
 * Product-related API calls
 */
export const products = {
  /**
   * Search for products
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} List of products
   */
  search: async (searchTerm = '', page = 1, perPage = 200) => {
    try {
      const cacheKey = `products_${searchTerm}_${page}_${perPage}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      
      // Return cached data if available and not too old (5 minutes)
      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);
        if (Date.now() - timestamp < 5 * 60 * 1000) { // 5 minutes cache
          console.log('Returning cached products data');
          return data;
        }
      }

      const params = {
        ...(searchTerm && { search: searchTerm }),
        page,
        per_page: perPage
      };
      
      // Add retry configuration for rate limiting
      const retryConfig = {
        retries: 3,
        initialDelay: 1000,
        maxDelay: 10000,
        backoffFactor: 2
      };
      
      const products = await get('/kiosk-products', params, { retryConfig });
      
      // Cache the response
      if (products && Array.isArray(products)) {
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            data: products,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn('Failed to cache products data:', e);
        }
        
        // Debug log for product data
        if (products.length > 0) {
          console.log('Products API response sample:', {
            productCount: products.length,
            firstProduct: {
              id: products[0].id,
              name: products[0].name,
              hasImages: !!products[0].images,
              imageCount: products[0].images?.length || 0,
              firstImage: products[0].images?.[0],
              imageSrc: products[0].images?.[0]?.src,
              imageSrcType: typeof products[0].images?.[0]?.src
            },
            cached: false
          });
        }
      }
      
      return products || [];
    } catch (error) {
      console.error('Error searching products:', error);
      // Return empty array instead of throwing to prevent UI crashes
      return [];
    }
  },

  /**
   * Get product by ID
   * @param {number|string} productId - Product ID
   * @returns {Promise<Object>} Product details
   */
  getById: async (productId) => {
    if (!productId) {
      throw new Error('Product ID is required');
    }
    return await get(`/kiosk-products/${productId}`);
  },

  /**
   * Get product variations
   * @param {number|string} productId - Product ID
   * @returns {Promise<Array>} List of product variations
   */
  getVariations: async (productId) => {
    if (!productId) {
      throw new Error('Product ID is required');
    }
    return await get(`/kiosk-products/${productId}/variations`);
  }
};

/**
 * Customer-related API calls
 */
export const customers = {
  /**
   * Search for customers (backend reads from DB, synced twice daily from WooCommerce)
   * @param {string} searchTerm - Search term (email, name, etc.)
   * @returns {Promise<Array>} List of customers
   */
  search: async (searchTerm = '') => {
    try {
      const response = await get('/api/users', { search: searchTerm, per_page: 100 });
      return Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
      console.error('Error searching customers:', error);
      throw error;
    }
  },
  
  /**
   * Search for users via API (backend reads from DB, synced twice daily from WooCommerce)
   * @param {string} searchTerm - Search term (email, name, phone, etc.)
   * @returns {Promise<Array>} List of matching users
   */
  searchUsers: async (searchTerm = '') => {
    try {
      const response = await get('/api/users', { search: searchTerm, per_page: 100 });
      return Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  },

  /**
   * Create a new customer
   * @param {Object} customerData - Customer data
   * @returns {Promise<Object>} Created customer
   */
  create: async (customerData) => {
    if (!customerData || typeof customerData !== 'object') {
      const error = new Error('Customer data is required');
      console.error('Customer creation error:', error);
      throw error;
    }
    
    // Create a clean copy of the customer data to avoid circular references
    const cleanCustomerData = {
      email: customerData.email,
      first_name: customerData.first_name,
      last_name: customerData.last_name,
      phone: customerData.phone,
      address1: customerData.address1,
      address2: customerData.address2,
      city: customerData.city,
      state: customerData.state,
      zipCode: customerData.zipCode,
      country: customerData.country,
      ...(customerData.meta && typeof customerData.meta === 'object' ? { meta: customerData.meta } : {})
    };
    
    console.log('Creating customer with data:', cleanCustomerData);
    
    try {
      const result = await post('/kiosk-customers', cleanCustomerData);
      console.log('Customer created successfully:', result);
      return result;
    } catch (error) {
      console.error('Error in customer creation:', error);
      throw error;
    }
  },

  /**
   * Get customer by ID
   * @param {number|string} customerId - Customer ID
   * @returns {Promise<Object>} Customer details
   */
  getById: async (customerId) => {
    if (!customerId) {
      throw new Error('Customer ID is required');
    }
    return await get(`/kiosk-users/${customerId}`);
  },

  /**
   * Update a customer
   * @param {number|string} customerId - Customer ID
   * @param {Object} updates - Updated customer data
   * @returns {Promise<Object>} Updated customer
   */
  update: async (customerId, updates) => {
    if (!customerId) {
      throw new Error('Customer ID is required');
    }
    if (!updates || typeof updates !== 'object') {
      throw new Error('Customer updates are required');
    }
    return await put(`/kiosk-users/${customerId}`, updates);
  }
};

/**
 * Order-related API calls
 */
export const orders = {
  /**
   * Create a new order
   * @param {Object} orderData - Order data
   * @returns {Promise<Object>} Created order
   */
  create: async (orderData) => {
    if (!orderData || typeof orderData !== 'object') {
      throw new Error('Order data is required');
    }
    
    // Add payment method details from environment variables
    const orderWithPayment = {
      ...orderData,
      payment_method: process.env.REACT_APP_WC_PAYMENT_METHOD || 'stripe_cc',
      payment_method_title: process.env.REACT_APP_WC_PAYMENT_METHOD_TITLE || 'Credit Card',
      set_paid: process.env.REACT_APP_WC_PAYMENT_SET_PAID === 'true',
      status: process.env.REACT_APP_WC_PAYMENT_STATUS || 'pending',
      customer_note: process.env.REACT_APP_WC_PAYMENT_CUSTOMER_NOTE || 'Kiosk order',
      currency: process.env.REACT_APP_WC_PAYMENT_CURRENCY || 'usd'
    };
    
    return await post('/kiosk-orders', orderWithPayment);
  },

  /**
   * Get order by ID
   * @param {number|string} orderId - Order ID
   * @returns {Promise<Object>} Order details
   */
  getById: async (orderId) => {
    if (!orderId) {
      throw new Error('Order ID is required');
    }
    return await get(`/kiosk-orders/${orderId}`);
  },

  /**
   * Update order status
   * @param {number|string} orderId - Order ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated order
   */
  updateStatus: async (orderId, status) => {
    if (!orderId) {
      throw new Error('Order ID is required');
    }
    if (!status) {
      throw new Error('Status is required');
    }
    return await put(`/kiosk-orders/${orderId}/status`, { status });
  },

  /**
   * Process a payment for an order
   * @param {number|string} orderId - Order ID
   * @param {Object} paymentData - Payment data
   * @returns {Promise<Object>} Payment result
   */
  processPayment: async (orderId, paymentData) => {
    if (!orderId) {
      throw new Error('Order ID is required');
    }
    if (!paymentData || typeof paymentData !== 'object') {
      throw new Error('Payment data is required');
    }
    return await post(`/kiosk-orders/${orderId}/pay`, paymentData);
  }
};

/**
 * Payment-related API calls
 */
export const payments = {
  /**
   * Create a payment intent
   * @param {number} amount - Amount in cents
   * @param {string} currency - Currency code (default: 'usd')
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} Payment intent
   */
  createIntent: async (amount, currency = 'usd', metadata = {}) => {
    if (!amount || isNaN(amount)) {
      throw new Error('Valid amount is required');
    }
    return await post('/payments/intents', {
      amount,
      currency,
      metadata
    });
  },

  /**
   * Confirm a payment intent
   * @param {string} paymentIntentId - Payment intent ID
   * @param {string} paymentMethodId - Payment method ID
   * @returns {Promise<Object>} Confirmation result
   */
  confirmIntent: async (paymentIntentId, paymentMethodId) => {
    if (!paymentIntentId) {
      throw new Error('Payment intent ID is required');
    }
    return await post(`/payments/intents/${paymentIntentId}/confirm`, {
      payment_method: paymentMethodId
    });
  }
};

// For backward compatibility
export default {
  products,
  customers,
  orders,
  payments,
  // Alias for backward compatibility
  searchProducts: products.search,
  searchCustomers: customers.search,
  createCustomer: customers.create,
  createOrder: orders.create,
  createPaymentIntent: payments.createIntent
};
